import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { selectStyles } from "../helper/SelectStyles";
import {
  getManagerById,
  saveManager,
  updateManager,
  uploadFile,
} from "../Apis/ManagerAPIs";
import { getRoles } from "../Apis/RoleAPIs";
import { getClientGroups } from "../Apis/ClientGroupAPIs";
import { toast } from "react-toastify";
import { getImageUrl } from "../Apis/api";

const EmployeeFormLayer = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [rolesList, setRolesList] = useState([]);
  const [groupsList, setGroupsList] = useState([]);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    role: "",
    password: "",
    confirmPassword: "",
    isActive: true,
    isWebAccessAllowed: false,
    profileImage: "",
    groupIds: [],
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [errors, setErrors] = useState({});

  const statusOptions = [
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
  ];

  const selectedRoleObj = rolesList.find((opt) => opt.value === formData.role);
  const isManagerSelected = selectedRoleObj?.label?.trim()?.toLowerCase() === "manager";

  useEffect(() => {
    const fetchInitialData = async () => {
      const roleRes = await getRoles();
      const roleData = roleRes.response?.data || roleRes.response;
      if (roleRes.status && Array.isArray(roleData)) {
        setRolesList(
          roleData.map((r) => ({ value: r._id || r.id, label: r.roleName })),
        );
      }

      let combinedGroups = [];
      const groupRes = await getClientGroups({ available: true });
      const groupData = groupRes.response?.data || groupRes.response;
      if (groupRes.status && Array.isArray(groupData)) {
        combinedGroups = [...groupData];
      }

      if (isEdit) {
        const assignedRes = await getClientGroups({ managerId: id, });
        const assignedData = assignedRes.response?.data || assignedRes.response;
        if (assignedRes.status && Array.isArray(assignedData)) {
          assignedData.forEach((g) => {
            if (!combinedGroups.some((cg) => (cg._id || cg.id) === (g._id || g.id))) {
              combinedGroups.push(g);
            }
          });
        }
      }

      setGroupsList(
        combinedGroups.map((g) => ({ value: g._id || g.id, label: g.groupName }))
      );

      if (isEdit) {
        setLoading(true);
        const res = await getManagerById(id);
        if (res.status && res.response) {
          const user = res.response?.data || res.response;
          const parsedGroups = (() => {
            const raw = user.groupIds || user.groupId || user.group || "";
            if (Array.isArray(raw)) {
              return raw.map(g => typeof g === "object" ? (g._id || g.id) : g).filter(Boolean);
            }
            if (raw) {
              return [typeof raw === "object" ? (raw._id || raw.id) : raw].filter(Boolean);
            }
            return [];
          })();

          setFormData({
            ...user,
            role: user.role?._id || user.role,
            password: "",
            confirmPassword: "",
            isActive: user.isActive ?? true,
            isWebAccessAllowed: user.isWebAccessAllowed ?? false,
            groupIds: parsedGroups,
          });
          if (user.profileImage) {
            setImagePreview(getImageUrl(user.profileImage));
          }
        }
        setLoading(false);
      }
    };
    fetchInitialData();
  }, [isEdit, id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "name") {
      if (value && !/^[a-zA-Z\s]*$/.test(value)) return;
    }
    if (name === "phoneNumber") {
      const cleaned = value.replace(/\D/g, "").slice(0, 10);
      setFormData((prev) => ({ ...prev, [name]: cleaned }));
      return;
    }
    if (name === "password" || name === "confirmPassword") {
      const cleaned = value.replace(/\D/g, "").slice(0, 4);
      setFormData((prev) => ({ ...prev, [name]: cleaned }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, val) => {
    setFormData((prev) => ({ ...prev, [name]: val ? val.value : "" }));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);

      // Instant Upload
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      formDataUpload.append("folder", "managers");
      const uploadRes = await uploadFile(formDataUpload);
      if (uploadRes.status) {
        const imagePath = uploadRes.response?.data?.path || uploadRes.response;
        setFormData((prev) => ({ ...prev, profileImage: imagePath }));
        toast.success("Image Uploaded Successfully");
      }
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is Required";

    if (!formData.email.trim()) {
      newErrors.email = "Email is Required";
    } else if (
      !/^[^\s@]+@[^\s@]+\.(com|in|co|net|org|edu|gov|co\.in|io|ai|app|dev)$/i.test(
        formData.email
      )
    ) {
      newErrors.email = "Invalid email format";
    }

    if (!formData.phoneNumber) {
      newErrors.phoneNumber = "Mobile Number is Required";
    } else if (formData.phoneNumber.length !== 10) {
      newErrors.phoneNumber = "Must be 10 digits";
    }

    if (!formData.role) {
      newErrors.role = "Role is Required";
    }

    if (!isEdit) {
      if (!formData.password) {
        newErrors.password = "Password is Required";
      } else if (formData.password.length !== 4) {
        newErrors.password = "Must be 4 digits";
      }

      if (!formData.confirmPassword) {
        newErrors.confirmPassword = "Confirm password is Required";
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = "Passwords do not match";
      }
    } else if (formData.password) {
      if (formData.password.length !== 4) {
        newErrors.password = "Must be 4 digits";
      }
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = "Passwords do not match";
      }
    }

    if (!imageFile && !imagePreview && !isEdit) {
      newErrors.profileImage = "Profile Image is Required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setSubmitting(true);

    const payload = {
      ...formData,
    };

    let res;
    if (isEdit) {
      res = await updateManager(id, payload);
    } else {
      res = await saveManager(payload);
    }

    if (res.status) {
      toast.success(`Manager ${isEdit ? "Updated" : "Added"} Successfully`);
      navigate("/employees");
    }
    setSubmitting(false);
  };

  const handlePinChange = (name, index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const currentPin = formData[name] || "";
    const pinArray = currentPin.split("");
    // Ensure array is 4 long
    while (pinArray.length < 4) pinArray.push("");

    pinArray[index] = digit;
    const newPin = pinArray.join("");
    setFormData((prev) => ({ ...prev, [name]: newPin }));

    if (digit && index < 3) {
      const nextInput = document.querySelector(
        `input[name="${name}-${index + 1}"]`,
      );
      if (nextInput) nextInput.focus();
    }
  };

  const handlePinKeyDown = (name, index, e) => {
    if (e.key === "Backspace" && !formData[name][index] && index > 0) {
      const prevInput = document.querySelector(
        `input[name="${name}-${index - 1}"]`,
      );
      if (prevInput) prevInput.focus();
    }
  };

  const renderPinInputs = (name, label, error, colClass = "col-md-4") => {
    const pin = formData[name] || "";
    const pinArray = pin.split("").concat(["", "", "", ""]).slice(0, 4);

    return (
      <div className={`${colClass} mt-24`}>
        <label className="form-label fw-bold">
          {label} {!isEdit && <span className="text-danger">*</span>}
        </label>
        <div className="d-flex gap-1">
          {pinArray.map((digit, idx) => (
            <input
              key={idx}
              type={
                name === "password"
                  ? showPassword
                    ? "text"
                    : "password"
                  : showConfirmPassword
                    ? "text"
                    : "password"
              }
              className={`form-control text-center radius-8 fw-bold ${error ? "border-danger" : ""}`}
              style={{ width: "40px", height: "40px", fontSize: "16px", padding: "4px" }}
              name={`${name}-${idx}`}
              value={digit}
              maxLength={1}
              onChange={(e) => handlePinChange(name, idx, e.target.value)}
              onKeyDown={(e) => handlePinKeyDown(name, idx, e)}
              autoComplete="new-password"
            />
          ))}
          <div className="d-flex align-items-center ms-1">
            <Icon
              icon={
                (name === "password" ? showPassword : showConfirmPassword)
                  ? "solar:eye-broken"
                  : "solar:eye-closed-broken"
              }
              className="cursor-pointer text-secondary-light"
              style={{ fontSize: "20px" }}
              onClick={() =>
                name === "password"
                  ? setShowPassword(!showPassword)
                  : setShowConfirmPassword(!showConfirmPassword)
              }
            />
          </div>
        </div>
        {error && (
          <div className="text-danger mt-1 small" style={{ fontSize: "12px" }}>
            {error}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="card h-100 p-0 radius-12">
      <div
        className="card-header border-bottom px-24 py-12 d-flex justify-content-between align-items-center"
        style={{
          background:
            "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)",
        }}
      >
        <h6 className="text-white pb-2 mb-0" style={{ fontWeight: "700" }}>
          {isEdit ? "EDIT MANAGER" : "ADD MANAGER"}
        </h6>
        <Link
          to="/employees"
          className="btn btn-sm d-flex align-items-center gap-1"
          style={{
            backgroundColor: "#f7e7da",
            color: "black",
            fontWeight: "600",
          }}
        >
          <Icon icon="solar:arrow-left-bold" /> List Manager
        </Link>
      </div>

      <div className="card-body p-24">
        <div className="pb-16 mb-24 border-bottom">
          <h5 className="mb-0" style={{ color: "#ed1c24", fontWeight: "700" }}>
            Basic Information
          </h5>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="row">
            {/* Profile Image Column */}
            <div className="col-lg-3 text-center mb-4 mb-lg-0">
              <label className="form-label fw-bold d-block text-start mb-12">
                Profile Image <span className="text-danger">*</span>
              </label>
              <div className="position-relative d-inline-block">
                <div
                  className={`radius-12 border d-flex justify-content-center align-items-center bg-light mb-16 ${errors.profileImage ? "border-danger" : ""}`}
                  style={{
                    width: "160px",
                    height: "160px",
                    overflow: "hidden",
                  }}
                >
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <Icon
                      icon="lucide:user"
                      className="text-secondary"
                      style={{ fontSize: "64px" }}
                    />
                  )}
                </div>
                <label
                  htmlFor="profileImage"
                  className="btn btn-outline-primary btn-sm px-32 radius-8 cursor-pointer"
                >
                  Choose
                  <input
                    type="file"
                    id="profileImage"
                    className="d-none"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </label>
                {errors.profileImage && (
                  <div
                    className="text-danger mt-12"
                    style={{ fontSize: "14px" }}
                  >
                    {errors.profileImage}
                  </div>
                )}
              </div>
            </div>

            {/* Fields Column */}
            <div className="col-lg-9">
              <div className="row gy-4 px-lg-20">
                {/* Row 1: Name, Email, and Mobile Number */}
                <div className="col-md-4">
                  <label className="form-label fw-bold">
                    Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control radius-8 ${errors.name ? "is-invalid" : ""}`}
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter Full Name"
                    autoComplete="off"
                  />
                  {errors.name && (
                    <div className="invalid-feedback">{errors.name}</div>
                  )}
                </div>

                <div className="col-md-4">
                  <label className="form-label fw-bold">
                    Email <span className="text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    className={`form-control radius-8 ${errors.email ? "is-invalid" : ""}`}
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter Email Address"
                    autoComplete="off"
                  />
                  {errors.email && (
                    <div className="invalid-feedback">{errors.email}</div>
                  )}
                </div>

                <div className="col-md-4">
                  <label className="form-label fw-bold">
                    Mobile Number <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control radius-8 ${errors.phoneNumber ? "is-invalid" : ""}`}
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    placeholder="Enter Phone Number"
                    autoComplete="off"
                  />
                  {errors.phoneNumber && (
                    <div className="invalid-feedback">{errors.phoneNumber}</div>
                  )}
                </div>

                {/* Row 2: Role, Password, and Confirm Password */}
                <div className="col-md-4 mt-24">
                  <label className="form-label fw-bold">
                    Role <span className="text-danger">*</span>
                  </label>
                  <Select
                    options={rolesList}
                    value={rolesList.find((opt) => opt.value === formData.role)}
                    onChange={(val) => handleSelectChange("role", val)}
                    styles={selectStyles(errors.role)}
                    placeholder="Select Role"
                  />
                  {errors.role && (
                    <div
                      className="text-danger mt-1 small"
                      style={{ fontSize: "12px" }}
                    >
                      {errors.role}
                    </div>
                  )}
                </div>

                {renderPinInputs(
                  "password",
                  isEdit ? "New Password" : "Password",
                  errors.password,
                  "col-md-4"
                )}
                {renderPinInputs(
                  "confirmPassword",
                  "Confirm Password",
                  errors.confirmPassword,
                  "col-md-4"
                )}

                {/* Row 3: Group and Status */}
                <div className="col-md-8 mt-24">
                  <label className="form-label fw-bold">
                    Group
                  </label>
                  <Select
                    options={groupsList}
                    value={groupsList.filter((opt) =>
                      Array.isArray(formData.groupIds)
                        ? formData.groupIds.includes(opt.value)
                        : formData.groupIds === opt.value
                    )}
                    onChange={(selected) => {
                      const values = selected ? selected.map((opt) => opt.value) : [];
                      setFormData((prev) => ({ ...prev, groupIds: values }));
                    }}
                    styles={selectStyles(errors.groupIds)}
                    placeholder="Select Group"
                    isClearable
                    isMulti
                  />
                  {errors.groupIds && (
                    <div
                      className="text-danger mt-1 small"
                      style={{ fontSize: "12px" }}
                    >
                      {errors.groupIds}
                    </div>
                  )}
                </div>

                <div className="col-md-4 mt-24">
                  <label className="form-label fw-bold">Status</label>
                  <Select
                    options={statusOptions}
                    value={statusOptions.find(
                      (opt) => opt.value === formData.isActive,
                    )}
                    onChange={(val) => handleSelectChange("isActive", val)}
                    styles={selectStyles()}
                    placeholder="Select Status"
                  />
                </div>

                {/* Web Access Checkbox for Manager Role */}
                {isManagerSelected && (
                  <div className="col-12 col-md-6 mt-24 d-flex align-items-end">
                    <div className="d-flex align-items-center gap-2 pb-2">
                      <input
                        type="checkbox"
                        id="isWebAccessAllowed"
                        checked={formData.isWebAccessAllowed || false}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            isWebAccessAllowed: e.target.checked,
                          }))
                        }
                        className="form-check-input mt-0 shadow-none cursor-pointer"
                        style={{
                          width: "20px",
                          height: "20px",
                          cursor: "pointer",
                        }}
                      />
                      <label
                        htmlFor="isWebAccessAllowed"
                        className="form-label fw-bold mb-0 cursor-pointer"
                        style={{
                          fontSize: "14px",
                          color: "#333",
                          whiteSpace: "nowrap",
                          cursor: "pointer",
                        }}
                      >
                        Allow Web Access (Admin Panel)
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Bottom Buttons */}
            <div className="col-12 mt-48 d-flex justify-content-end gap-3 px-20">
              <button
                type="button"
                onClick={() => navigate("/employees")}
                className="btn radius-8 px-40 py-11"
                style={{
                  backgroundColor: "white",
                  color: "#d9534f",
                  fontWeight: "700",
                  border: "1px solid #d9534f",
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn radius-8 px-48 py-11"
                disabled={submitting}
                style={{
                  backgroundColor: "#ed1c24",
                  color: "white",
                  fontWeight: "700",
                }}
              >
                {submitting ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmployeeFormLayer;
